import React from "react";
import ResearchProjectCard from "../components/ResearchProjectCard";
import LinkedInPosts from "../components/LinkedInPosts";
import { currentProjects } from "../data/portfolioData";

interface HomeProps {
  setActiveSection: (section: string) => void;
}

const Home: React.FC<HomeProps> = ({ setActiveSection }) => {
  const featuredProjects = currentProjects.slice(0, 3);

  const handleNavigationClick = (section: string) => {
    setActiveSection(section);
  };

  return (
    <section className="section active">
      <div className="hero">
        <h1>Dr. Alex Mari</h1>
        <p className="subtitle">
          Professor & Researcher — Marketer & Entrepreneur — Consultant &
          Speaker
        </p>

        <img
          src="/images/Alex-Mari-PhD-Professor-Marketing.jpg"
          alt="Dr. Alex Mari"
          className="hero-image"
        />

        <div className="quote">
          <p>
            <em>
              "The greater danger for most of us lies not in setting our aim too
              high and falling short; but in setting our aim too low, and
              achieving our mark."
            </em>
          </p>
          <p>
            <strong>― Michelangelo Buonarroti</strong>
          </p>
        </div>
      </div>

      <div className="bio">
        <h2>About</h2>
        <p>
          Hi, I'm <span className="professional-title">Alex Mari, Ph.D.</span> —{" "}
          <span className="professional-title">Assistant Professor</span> of
          Marketing at the{" "}
          <a
            href="https://aubh.edu.bh"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            <strong>American University of Bahrain</strong>
          </a>
          . My expertise lies at the intersection of marketing, strategic
          management, and digital innovation. Helping people grow through talent
          development and personal branding is what drives me.
        </p>

        <p>
          I am the <span className="professional-title">Co-founder</span> of the{" "}
          <a
            href="https://www.business.uzh.ch/de/research/professorships/market-research/research/AI-Empathy-Research-Initiative.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            <strong>AI Empathy Lab</strong>
          </a>
          , which explores how empathic voice assistants influence
          decision-making processes, particularly in retail and healthcare
          contexts.{" "}
          <a
            href="#research"
            onClick={(e) => {
              e.preventDefault();
              handleNavigationClick("research");
            }}
            className="navigation-arrow"
          >
            {">>"}
          </a>{" "}
          Additionally, I am the{" "}
          <span className="professional-title">Founder</span> of{" "}
          <a
            href="https://www.caryally.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            <strong>Cary Ally</strong>
          </a>
          , an AI companion designed to support the emotional well-being of
          older adults.
        </p>

        <p>
          Beyond my role in Bahrain, I serve as a{" "}
          <span className="professional-title">Visiting Professor</span> at the{" "}
          <a
            href="https://docenti.unicatt.it/ppd2/en/docenti/101300/alessandro-mari/profilo"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            <strong>Catholic University</strong>
          </a>{" "}
          in Milan and as an{" "}
          <span className="professional-title">Adjunct Faculty</span> at{" "}
          <a
            href="https://www.sbs.edu/team/alex-mari/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#000",
              textDecoration: "underline",
              fontWeight: "bold",
            }}
          >
            <strong>SBS</strong>
          </a>{" "}
          in Zurich. I am a regular{" "}
          <span className="professional-title">guest speaker</span> at leading
          business schools, corporate events, and industry conferences.{" "}
          <a
            href="#engagement"
            onClick={(e) => {
              e.preventDefault();
              handleNavigationClick("engagement");
            }}
            className="navigation-arrow"
          >
            {">>"}
          </a>
        </p>

        <p>
          I hold a{" "}
          <span className="professional-title">
            Ph.D. in Business Administration
          </span>{" "}
          (Marketing) from the University of Zurich (UZH) and an{" "}
          <span className="professional-title">MSc in Marketing</span> from the
          University of Lugano (USI). During my studies, I was a{" "}
          <span className="professional-title">visiting Ph.D. fellow</span> at
          Saïd Business School, University of Oxford and earned several
          certifications.{" "}
          <a
            href="#experience"
            onClick={(e) => {
              e.preventDefault();
              handleNavigationClick("experience");
            }}
            className="navigation-arrow"
          >
            {">>"}
          </a>
        </p>

        <p>
          Before entering academia full-time, I held leadership roles as a
          startup <span className="professional-title">CEO</span> (Fennex),{" "}
          <span className="professional-title">
            Global Digital Marketing Director
          </span>{" "}
          (Sonova), <span className="professional-title">Brand Manager</span>{" "}
          (Procter & Gamble), and{" "}
          <span className="professional-title">Digital Lead</span> (TIM),
          gaining deep insights into the technology, medical devices, and
          consumer goods industries.{" "}
          <a
            href="#experience"
            onClick={(e) => {
              e.preventDefault();
              handleNavigationClick("experience");
            }}
            className="navigation-arrow"
          >
            {">>"}
          </a>
        </p>

        <p className="bold-text">
          <em>
            I am always open to new ideas and collaborations — feel free to
            reach out!
          </em>
        </p>

        <div className="research-interests">
          <h3>Research Interests</h3>
          <div className="publication-list">
            <div className="publication-item">
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <strong>Technology impact</strong>: transformative consumer
                technologies and their impact on managerial and consumer
                decision-making.
              </span>
            </div>
            <div className="publication-item">
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <strong>Marketing trends</strong>: emerging phenomena affecting
                marketing practice such as conversational AI, generative AI and
                automation.
              </span>
            </div>
            <div className="publication-item">
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <strong>Platform ecosystems</strong>: adoption drivers and
                strategic management decisions in technological multi-sided
                platform ecosystems.
              </span>
            </div>
            <div className="publication-item">
              <span
                style={{ minWidth: "25px", fontSize: "16px", flexShrink: 0 }}
              >
                –
              </span>
              <span style={{ paddingLeft: "20px", flex: 1 }}>
                <strong>Research methods</strong>: experimental and qualitative
                methods in consumer-, machine-, and management-oriented research
                projects.
              </span>
            </div>
          </div>
        </div>

        <div className="keywords">
          <h3>Keywords</h3>
          <p>
            AI Empathy, Voice Assistants, Voice Commerce, Decision-making,
            Emotions, Sustainable Shopping, AI Persuasion, AI Companion, AI
            Memory, Technology FOMO.
          </p>
        </div>

        <LinkedInPosts />

        <h2>Research</h2>

        <div className="home-publications">
          <h3>Published Research</h3>
          <div className="publication-list">
            <div className="publication-item">
              <strong>1.</strong> <strong>Mari A.</strong>, Mandelli A. &
              Algesheimer R. (2025){" "}
              <em>
                AI-Assisted Shopping on Voice Assistants: The
                Efficiency-Autonomy Consumer Decision Trade-Off
              </em>
              . Journal of Interactive Marketing.{" "}
              <a
                href="/publications/AI-Assisted-Shopping-on-Voice-Assistants-The-Efficiency-Autonomy-Consumer-Decision-Trade-Off_2025_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://journals.sagepub.com/doi/10.1177/10949968251400616"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
            <div className="publication-item">
              <strong>2.</strong> <strong>Mari A.</strong>, Mandelli A. &
              Algesheimer R. (2024){" "}
              <em>
                Empathic voice assistants: Enhancing consumer responses in voice
                commerce
              </em>
              . Journal of Business Research, 175, 114566.{" "}
              <a
                href="/publications/Empathic-voice-assistants-Enhancing-consumer-responses-in-voice-commerce_2024_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://www.sciencedirect.com/science/article/pii/S0148296324000705"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
            <div className="publication-item">
              <strong>3.</strong> <strong>Mari A.</strong>, Mandelli A. &
              Algesheimer R. (2025){" "}
              <em>
                Harnessing AI Voice Assistants for Digital Corporate
                Communication
              </em>
              . Corporate Communication Review, 1 (1), 40-45.{" "}
              <a
                href="/publications/Harnessing-AI-voice-assistants-for-digital-corporate-communication_2025_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://www.corpcommreview.com/corpcommreview/article/view/10/9"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
          </div>
          <p style={{ marginTop: "1rem" }}>
            <strong>
              <a
                href="#publications"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigationClick("publications");
                }}
                className="navigation-arrow"
              >
                {">>"} Complete list
              </a>
            </strong>
          </p>
        </div>

        <div className="home-research">
          <h3>Completed Research</h3>
          <div className="publication-list">
            <div className="publication-item">
              <strong>1.</strong> <strong>Mari, A.</strong>, Uysal, D. E.,
              Alabed, A., Efthymiou, F., & Brooks, J. (under review){" "}
              <em>
                Generative AI-Enabled Empathy and Persuasion in Voice
                Assistants: Impact on Sustainable Consumer Choice
              </em>
              .{" "}
              <a
                href="/publications/Generative-AI-Enabled-Empathy-and-Persuasion-in-Voice-Assistants-Impact-on-Sustainable-Consumer-Choice_2025_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5420076"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
            <div className="publication-item">
              <strong>2.</strong> <strong>Mari A.</strong>, Mandelli A. &
              Algesheimer R. (under review){" "}
              <em>
                Fear of Missing Out (FOMO) on Emerging AI Technology: Biased and
                Unbiased Adoption Decision Making
              </em>
              .{" "}
              <a
                href="/publications/Fear-of-Missing-Out-FOMO-on-Emerging-AI-Technology-Biased-and-Unbiased-Adoption-Decision-Making_2025_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=4736154"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
            <div className="publication-item">
              <strong>3.</strong> Efthymiou F., <strong>Mari A.</strong>, Uysal
              E. & Brooks, J. (under review){" "}
              <em>
                No Hard Feelings: The Protective Power of AI Empathy During
                Service Interaction Failures
              </em>
              .{" "}
              <a
                href="/publications/No-Hard-Feelings-The-Protective-Power-of-AI-Empathy-During-Service-Interaction-Failures_2025_Dr-Alex-Mari.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="pdf-link"
              >
                PDF
              </a>{" "}
              <a
                href="https://doi.org/10.31234/osf.io/4n9xy_v1"
                target="_blank"
                rel="noopener noreferrer"
                className="navigation-arrow"
              >
                {">>"}
              </a>
            </div>
          </div>
          <p>
            <strong>
              <a
                href="#publications"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigationClick("publications");
                }}
                className="navigation-arrow"
              >
                {">>"} Complete list
              </a>
            </strong>
          </p>
        </div>

        <div className="current-research">
          <h3>Current Research</h3>
          {featuredProjects.map((project) => (
            <ResearchProjectCard key={project.id} project={project} />
          ))}
          <p>
            <strong>
              <a
                href="#research"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigationClick("research");
                }}
                className="navigation-arrow"
              >
                {">>"} AI Empathy LAB
              </a>
            </strong>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Home;
